/* Unified Inbox — ScaleIQ-style 3-column conversation view
 *
 *   Tabs           : Conversations · Upcoming Messages · Sent Messages
 *   Left column    : Accounts (workspace users + their connected channels)   → AccountsSidebar
 *   Middle column  : Prospects (one row per lead/candidate with last msg)    → ProspectList
 *   Right column   : Full thread + Tag dropdown + Composer                   → ConversationPanel
 *
 *   Used by both /app/sales/outreach and /app/recruit/outreach via the
 *   `pillar` prop. All data flows through /api/inbox/*.
 *
 *   This file is the orchestrator: it owns all inbox state + API handlers
 *   and passes them down. UI lives in the sibling component files.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { Mic } from "lucide-react";
import RejectReasonModal from "@/components/common/RejectReasonModal";
import { TABS, LANGUAGES, tagMeta } from "./constants";
import AccountsSidebar from "./AccountsSidebar";
import ProspectList from "./ProspectList";
import ConversationPanel from "./ConversationPanel";

export default function UnifiedInbox({ pillar = "sales" }) {
    const [tab, setTab] = useState("conversations");
    const [accounts, setAccounts] = useState([]);
    const [channelsConnected, setChannelsConnected] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState(null); // null = all
    const [threads, setThreads] = useState([]);
    const [tabCounts, setTabCounts] = useState({ conversations: 0, upcoming: 0, sent: 0 });
    const [campaigns, setCampaigns] = useState([]);
    const [tagFilter, setTagFilter] = useState("");
    const [campaignFilter, setCampaignFilter] = useState("");
    const [search, setSearch] = useState("");
    const [includeNoReply, setIncludeNoReply] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [thread, setThread] = useState(null);
    const [loadingThreads, setLoadingThreads] = useState(true);
    const [loadingThread, setLoadingThread] = useState(false);

    // ---- Load Accounts (once) ----
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/inbox/accounts");
                setAccounts(r.data.accounts || []);
                setChannelsConnected(r.data.channels_connected || []);
            } catch { /* silent */ }
        })();
    }, []);

    // ---- Load threads on filter change ----
    const loadThreads = async () => {
        // Lege account-selectie ("Unselect all") = bewust géén accounts → toon niets.
        if (selectedUserIds !== null && selectedUserIds.length === 0) {
            setThreads([]);
            setTabCounts({ conversations: 0, upcoming: 0, sent: 0 });
            setLoadingThreads(false);
            return;
        }
        setLoadingThreads(true);
        try {
            const params = { pillar, tab, limit: 200 };
            if (search.trim()) params.search = search.trim();
            if (tagFilter) params.tag = tagFilter;
            if (campaignFilter) params.campaign = campaignFilter;
            if (selectedUserIds && selectedUserIds.length > 0) params.account_user_ids = selectedUserIds.join(",");
            if (tab === "conversations" && includeNoReply) params.include_no_reply = true;
            const r = await api.get("/inbox/threads", { params });
            setThreads(r.data.threads || []);
            if (r.data.tab_counts) setTabCounts(r.data.tab_counts);
            setCampaigns(r.data.campaigns || []);
            // Auto-select the first thread if nothing selected
            if (!activeId && (r.data.threads || []).length > 0) {
                setActiveId(r.data.threads[0].prospect_id);
            }
        } catch { /* silent */ }
        finally { setLoadingThreads(false); }
    };
    useEffect(() => {
        loadThreads();
        const t = setInterval(loadThreads, 20000);
        return () => clearInterval(t);
    }, [pillar, tab, tagFilter, campaignFilter, selectedUserIds, includeNoReply]);
    // search is debounced
    const sRef = useRef();
    useEffect(() => {
        clearTimeout(sRef.current);
        sRef.current = setTimeout(() => loadThreads(), 300);
        return () => clearTimeout(sRef.current);
    }, [search]);

    // ---- Load full thread on selection ----
    useEffect(() => {
        if (!activeId) { setThread(null); return; }
        setLoadingThread(true);
        api.get(`/inbox/thread/${activeId}`, { params: { pillar } })
            .then((r) => setThread(r.data))
            .catch(() => setThread(null))
            .finally(() => setLoadingThread(false));
    }, [activeId, pillar]);

    const toggleUser = (uid) => {
        setSelectedUserIds((prev) => {
            if (prev === null) {
                // start with the clicked uid only
                return accounts.length > 1 ? [uid] : null;
            }
            const isOn = prev.includes(uid);
            const next = isOn ? prev.filter((x) => x !== uid) : [...prev, uid];
            return next.length === 0 ? [] : next;
        });
    };
    const selectAll = () => setSelectedUserIds(null);
    const selectNone = () => setSelectedUserIds([]);

    // Herlaad de actieve thread + threadlijst (na elke mutatie).
    const refreshThread = async () => {
        if (!thread?.prospect?.id) return;
        const r = await api.get(`/inbox/thread/${thread.prospect.id}`, { params: { pillar } });
        setThread(r.data);
    };

    const setTag = async (tagId) => {
        if (!thread?.prospect?.id) return;
        try {
            await api.patch(`/inbox/thread/${thread.prospect.id}/tag`, { tag: tagId }, { params: { pillar } });
            setThread((t) => t ? ({ ...t, prospect: { ...t.prospect, inbox_tag: tagId } }) : t);
            setThreads((arr) => arr.map((th) => th.prospect_id === thread.prospect.id ? { ...th, inbox_tag: tagId } : th));
            toast.success(i18n.t("inbox.tagged", { tag: tagMeta(tagId).label }));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("inbox.tag_failed")); }
    };

    const sendReply = async (channel, body, subject) => {
        if (!thread?.prospect?.id || !body.trim()) return;
        try {
            await api.post(`/inbox/thread/${thread.prospect.id}/reply`, { channel, body, subject }, { params: { pillar } });
            toast.success(i18n.t("inbox.msg_created"));
            await refreshThread();
            loadThreads();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("inbox.send_failed")); }
    };

    // Approve / decline pending outreach directly from the bubble.
    // Reject uses the shared RejectReasonModal so we always capture a reason
    // for agent-memory training (see backend/agent_memory_routes).
    const [pendingReject, setPendingReject] = useState(null);   // { msgId, body }
    const approveMessage = async (msgId) => {
        try {
            await api.post(`/outreach/${msgId}/approve`);
            toast.success(i18n.t("inbox.approved"));
            await refreshThread(); loadThreads();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("inbox.approve_failed")); }
    };
    const openRejectModal = (msg) => setPendingReject({ msgId: msg.msg_id || msg.id, body: msg.body || "" });

    // Send an approved/scheduled message. Backend may schedule it instead of
    // sending immediately when Auto-Throttle pacing is on.
    const sendMessage = async (msgId) => {
        try {
            const r = await api.post(`/outreach/${msgId}/send`);
            if (r.data?.status === "scheduled") {
                const t = r.data.scheduled_send_at
                    ? new Date(r.data.scheduled_send_at).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" })
                    : "";
                toast.success(i18n.t("inbox.scheduled_ok", { time: t }));
            } else {
                toast.success(i18n.t("inbox.sent_ok"));
            }
            await refreshThread(); loadThreads();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("inbox.send_failed")); }
    };
    // Force-send a scheduled message right now.
    const sendNowMessage = async (msgId) => {
        try {
            await api.post(`/outreach/${msgId}/send-now`);
            toast.success(i18n.t("inbox.sent_ok"));
            await refreshThread(); loadThreads();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("inbox.send_failed")); }
    };
    // Submit a draft for approval (may auto-approve depending on workspace).
    const submitMessage = async (msgId) => {
        try {
            const r = await api.post(`/outreach/${msgId}/submit`);
            toast.success(r.data?.status === "approved"
                ? i18n.t("inbox.submit_approved")
                : i18n.t("inbox.submit_ok"));
            await refreshThread(); loadThreads();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("inbox.submit_failed")); }
    };
    const confirmReject = async (reason, category, remember) => {
        if (!pendingReject) return;
        try {
            await api.post(`/outreach/${pendingReject.msgId}/reject`, {
                reason, category, remember,
            });
            toast.success(i18n.t("inbox.rejected"));
            await refreshThread(); loadThreads();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("inbox.reject_failed")); }
    };

    // Edit a pending message inline (body / subject). Auto-refreshes.
    const saveEdit = async (msgId, { body, subject }) => {
        try {
            const payload = {};
            if (body !== undefined) payload.body = body;
            if (subject !== undefined) payload.subject = subject;
            await api.patch(`/outreach/${msgId}`, payload);
            toast.success(i18n.t("inbox.edit_saved"));
            await refreshThread();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("inbox.save_failed")); }
    };

    // Translate + apply — first fetches the translation, then applies it as
    // the new body (which auto-demotes pending→draft, prompting re-review).
    const translateAndApply = async (msgId, lang) => {
        try {
            const r = await api.post(`/outreach/${msgId}/translate`, { lang });
            await api.post(`/outreach/${msgId}/apply-translation`, { lang });
            toast.success(i18n.t("inbox.translated", { lang: LANGUAGES.find((l) => l.code === lang)?.label || lang }));
            await refreshThread();
            return r.data;
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("inbox.translate_failed")); }
    };

    // Hergenereer een concept in de signaal-bewuste stijl (Claude verwerkt
    // de concrete aanleiding). Bericht gaat terug naar draft voor re-review.
    const regenerateMessage = async (msgId) => {
        try {
            const r = await api.post(`/outreach/${msgId}/regenerate`);
            const sig = r.data?.signal_used;
            toast.success(sig ? i18n.t("inbox.regenerated_with", { signal: sig.slice(0, 70) }) : i18n.t("inbox.regenerated"));
            await refreshThread(); loadThreads();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("inbox.regen_failed")); }
    };

    const selectedSet = useMemo(() => selectedUserIds === null ? null : new Set(selectedUserIds), [selectedUserIds]);

    return (
        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] overflow-hidden" data-testid="unified-inbox">
            {/* Tabs row */}
            <div className="px-5 pt-4 border-b border-white/8 flex items-center gap-1">
                {TABS.map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                        className={`relative px-4 py-3 text-sm font-medium transition-colors ${tab === t.id ? "text-[var(--jr-brand)]" : "text-[var(--jr-text-2)] hover:text-white"}`}>
                        {i18n.t(t.labelKey)}
                        <span className="ml-1.5 text-[10px] font-mono text-[var(--jr-text-3)]">{tabCounts[t.id] ?? 0}</span>
                        {tab === t.id && <span className="absolute inset-x-2 -bottom-px h-[2px] bg-[var(--jr-brand)] rounded-full" />}
                    </button>
                ))}
            </div>

            {/* 3-column grid — hoogte volgt de viewport (dvh) zodat de panes
             *  intern scrollen i.p.v. onder de vouw te verdwijnen. Op mobiel
             *  stapelen de secties met eigen max-hoogtes. */}
            <div className="grid grid-cols-1 md:grid-cols-[260px_360px_1fr] md:grid-rows-[minmax(0,1fr)] gap-0 md:h-[calc(100dvh-260px)] md:min-h-[420px]">
                {/* ===== LEFT — Accounts ===== */}
                <AccountsSidebar
                    accounts={accounts}
                    channelsConnected={channelsConnected}
                    selectedSet={selectedSet}
                    onToggleUser={toggleUser}
                    onSelectAll={selectAll}
                    onSelectNone={selectNone}
                />

                {/* ===== MIDDLE — Prospects ===== */}
                <ProspectList
                    tab={tab}
                    threads={threads}
                    loadingThreads={loadingThreads}
                    accounts={accounts}
                    campaigns={campaigns}
                    activeId={activeId}
                    onSelect={setActiveId}
                    search={search} setSearch={setSearch}
                    tagFilter={tagFilter} setTagFilter={setTagFilter}
                    campaignFilter={campaignFilter} setCampaignFilter={setCampaignFilter}
                    includeNoReply={includeNoReply} setIncludeNoReply={setIncludeNoReply}
                />

                {/* ===== RIGHT — Conversation ===== */}
                <section className="flex flex-col min-w-0 min-h-0 bg-[var(--jr-surface)]/30 overflow-hidden h-[75dvh] md:h-full" data-testid="inbox-conversation">
                    {!thread ? (
                        <div className="m-auto text-center text-[var(--jr-text-3)] p-10">
                            <Mic size={28} className="mx-auto mb-3 text-[var(--jr-brand)]" />
                            <p className="text-sm">{i18n.t("inbox.select_prospect")}</p>
                        </div>
                    ) : (
                        <ConversationPanel
                            pillar={pillar}
                            thread={thread}
                            loading={loadingThread}
                            channelsConnected={channelsConnected}
                            onSetTag={setTag}
                            onSendReply={sendReply}
                            onApprove={approveMessage}
                            onReject={openRejectModal}
                            onSaveEdit={saveEdit}
                            onTranslate={translateAndApply}
                            onRegenerate={regenerateMessage}
                            onSend={sendMessage}
                            onSendNow={sendNowMessage}
                            onSubmit={submitMessage}
                        />
                    )}
                </section>
            </div>
            {/* Reject modal — mounted once at the root of the inbox */}
            <RejectReasonModal
                open={!!pendingReject}
                onClose={() => setPendingReject(null)}
                title={i18n.t("inbox.reject_title")}
                subtitle={thread?.prospect ? i18n.t("inbox.to_name", { name: thread.prospect.name }) : ""}
                originalSnippet={pendingReject?.body || ""}
                onConfirm={confirmReject}
            />
        </div>
    );
}
